// «Внештатный» (R-A9): a contact gets work sections — a person of the team without a team login, who enters from their personal page.

import { useMemo, useState } from 'react';
import { recordTitle, uuidv7, type Id } from '@confdeck/core-shared';
import { PickList, type PickOption } from '../ui/PickList.tsx';
import { useMeta, useSync } from '../sync/react.tsx';
import { Button, Dialog } from '../ui/primitives.tsx';
import s from './team.module.css';

/** People with a personal page: the type an app declares (`people` in its manifest); the core does not name it. */
export function ExternalDialog({ onClose, onDone }: { onClose: () => void; onDone: (id: Id) => void }) {
  const meta = useMeta();
  const client = useSync();
  const [error, setError] = useState<string | null>(null);
  const taken = new Set(meta.users().filter((u) => u.external).map((u) => u.contactId));
  const kinds = new Set(client.header?.people ?? []);
  const people = useMemo(() => meta.types().filter((t) => kinds.has(t.id)).flatMap((t) => {
    const email = meta.fields(t.id).find((f) => f.fieldType === 'email');
    if (!email) return [];
    return [...client.store.recordIds(t.id)].map((id) => client.store.record(id)).filter((r) => r && !r.archivedAt && r.data[email.key] && !taken.has(r.id))
      .map((r) => ({ id: r!.id, label: recordTitle(r!, meta), hint: String(r!.data[email.key]), email: String(r!.data[email.key]), group: t.nameMany }));
  }), [meta, client.store]);
  const options = (q: string): PickOption[] => {
    const k = q.trim().toLowerCase();
    return (k ? people.filter((p) => p.label.toLowerCase().includes(k) || p.email.toLowerCase().includes(k)) : people).slice(0, 50);
  };
  const pick = async (id: Id) => {
    const p = people.find((x) => x.id === id);
    if (!p) return;
    const uid = uuidv7();
    const r = await client.run('user.addExternal', { id: uid, contactId: p.id, name: p.label, email: p.email });
    if (r.ok) onDone(uid); else setError(r.error.message);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} label="Внештатный">
      <div className={s.dialog}>
        <h2 className={s.dialogTitle}>Внештатный</h2>
        <p className={s.help}>Например, волонтёр события. Он не входит в админку: в его личном кабинете появится «Работа» — те же экраны, но только разделы и действия его ролей.</p>
        <PickList options={options} selected={[]} placeholder="Найти контакт по имени или email" onPick={(id) => void pick(id)} emptyText="Контактов с email не нашлось" />
        {error && <p className={s.error} role="alert">{error}</p>}
        <p className={s.help}>После выбора выдайте роль, например «Волонтёр», и ограничьте её событием.</p>
        <div className={s.actions}><span className={s.spacer} /><Button onClick={onClose}>Отмена</Button></div>
      </div>
    </Dialog>
  );
}
